import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { issuesAPI } from '../services/api';

// Add fade-in animation for modal
const modalAnimation = `
  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
  @keyframes backdropFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  @keyframes modalGlow {
    0%, 100% {
      box-shadow: 0 25px 80px rgba(0, 0, 0, 0.5), 0 0 0 0 rgba(102, 126, 234, 0.4);
    }
    50% {
      box-shadow: 0 25px 80px rgba(0, 0, 0, 0.5), 0 0 0 8px rgba(102, 126, 234, 0.2);
    }
  }
`;

// Inject the animation styles
if (typeof document !== 'undefined') {
  const styleId = 'report-issue-modal-animation';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = modalAnimation;
    document.head.appendChild(style);
  }
}

function ReportIssueModal({ 
  isOpen, 
  onClose, 
  questionData,
  sourceType,
  sourceId,
  questionPath,
  questionId,
  questionIndex,
  metadata 
}) {
  const [issueType, setIssueType] = useState('wrong_answer');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const modalBackdropRef = useRef(null);

  // Prevent body scroll when modal is open and scroll to top
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      
      // Scroll to top immediately when modal opens
      window.scrollTo(0, 0);
      
      // Scroll the modal backdrop to top as well
      if (modalBackdropRef.current) {
        modalBackdropRef.current.scrollTop = 0;
      }
      
      // Fix body position to prevent scrolling
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore scroll position
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      return isImage || isVideo;
    });
    
    if (validFiles.length !== files.length) {
      setError('Only images and videos are allowed');
      setTimeout(() => setError(''), 3000);
    }
    
    setAttachments(prev => [...prev, ...validFiles].slice(0, 5)); // Max 5 files
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!description.trim()) {
      setError('Please describe the issue');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('sourceType', sourceType);
      formData.append('sourceId', sourceId);
      formData.append('questionPath', questionPath);
      if (questionId) formData.append('questionId', questionId);
      if (questionIndex !== undefined) formData.append('questionIndex', questionIndex);
      formData.append('questionSnapshot', JSON.stringify(questionData));
      formData.append('issueType', issueType);
      formData.append('description', description.trim());
      formData.append('metadata', JSON.stringify({
        ...metadata,
        userAgent: navigator.userAgent,
        timestamp: metadata?.timestamp || new Date().toISOString()
      }));

      // Append files
      attachments.forEach(file => {
        formData.append('attachments', file);
      });

      await issuesAPI.reportIssue(formData);

      alert('Issue reported successfully! An admin will review it shortly.');
      onClose();
      setDescription('');
      setIssueType('wrong_answer');
      setAttachments([]);
    } catch (err) {
      console.error('Error reporting issue:', err);
      console.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
        config: err.config
      });
      
      let errorMessage = 'Failed to report issue. Please try again.';
      if (err.response?.status === 404) {
        errorMessage = 'Issue reporting endpoint not found. Please ensure the backend server is running.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const modalContent = (
    <div 
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        padding: '20px',
        overflowY: 'auto',
        animation: 'backdropFadeIn 0.2s ease-out'
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.5)',
          margin: '20px 0',
          position: 'relative',
          zIndex: 1000000,
          animation: 'modalFadeIn 0.3s ease-out'
        }}
      >
        {/* Header - Fixed */}
        <div style={{
          padding: '18px 24px',
          borderBottom: '2px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          backgroundColor: '#fff',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', color: '#fff', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '22px' }}>🚩</span>
            <span>Report Issue with Question</span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#fff',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: '1',
              borderRadius: '6px',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            ×
          </button>
        </div>

        {/* Body - Scrollable */}
        <div style={{
          overflowY: 'auto',
          flex: 1,
          padding: '0'
        }}>
          <form id="report-issue-form" onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {/* Question Preview */}
          {questionData && (
            <div style={{
              padding: '14px',
              backgroundColor: '#f7fafc',
              borderRadius: '8px',
              marginBottom: '18px',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ fontSize: '12px', color: '#718096', marginBottom: '6px', fontWeight: '600' }}>
                Question:
              </div>
              <div style={{ fontSize: '13px', color: '#2d3748', marginBottom: '10px', lineHeight: '1.5' }}>
                {questionData.question || 'N/A'}
              </div>
              {questionData.options && questionData.options.length > 0 && (
                <div>
                  <div style={{ fontSize: '12px', color: '#718096', marginBottom: '6px', fontWeight: '600' }}>
                    Options:
                  </div>
                  {questionData.options.map((option, idx) => (
                    <div 
                      key={idx}
                      style={{
                        fontSize: '12px',
                        color: '#4a5568',
                        padding: '6px 10px',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        marginBottom: '4px',
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      {idx + 1}. {option}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Issue Type */}
          <div style={{ marginBottom: '18px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '600',
              color: '#2d3748',
              marginBottom: '6px'
            }}>
              Issue Type <span style={{ color: '#e53e3e' }}>*</span>
            </label>
            <select
              value={issueType}
              onChange={(e) => setIssueType(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #cbd5e0',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#2d3748',
                backgroundColor: '#fff',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="wrong_answer">Wrong Answer Marked</option>
              <option value="ambiguous_question">Ambiguous Question</option>
              <option value="typo">Typo/Grammar Error</option>
              <option value="missing_option">Missing Option</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Description */}
          <div style={{ marginBottom: '18px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '600',
              color: '#2d3748',
              marginBottom: '6px'
            }}>
              Description <span style={{ color: '#e53e3e' }}>*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please describe the issue in detail..."
              required
              rows="4"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #cbd5e0',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#2d3748',
                resize: 'vertical',
                fontFamily: 'inherit',
                outline: 'none',
                lineHeight: '1.5'
              }}
            />
            <div style={{ fontSize: '11px', color: '#718096', marginTop: '4px' }}>
              Be specific about what's wrong and what the correct answer should be
            </div>
          </div>

          {/* Attachments */}
          <div style={{ marginBottom: '18px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '600',
              color: '#2d3748',
              marginBottom: '6px'
            }}>
              Attachments (Optional)
            </label>
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileChange}
              style={{
                width: '100%',
                padding: '9px 12px',
                border: '1px solid #cbd5e0',
                borderRadius: '6px',
                fontSize: '13px',
                backgroundColor: '#fff',
                cursor: 'pointer'
              }}
            />
            <div style={{ fontSize: '11px', color: '#718096', marginTop: '4px' }}>
              Upload images or videos to help explain the issue (Max 5 files, 50MB each)
            </div>
            
            {/* Preview attachments */}
            {attachments.length > 0 && (
              <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {attachments.map((file, index) => (
                  <div key={index} style={{
                    position: 'relative',
                    padding: '6px 10px',
                    backgroundColor: '#f7fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '12px'
                  }}>
                    <span style={{ color: '#4a5568' }}>
                      {file.type.startsWith('image/') ? '🖼️' : '🎥'} {file.name.length > 18 ? file.name.substring(0, 18) + '...' : file.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#e53e3e',
                        cursor: 'pointer',
                        fontSize: '18px',
                        padding: '0',
                        lineHeight: '1',
                        fontWeight: 'bold'
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              padding: '10px 12px',
              backgroundColor: '#fed7d7',
              color: '#c53030',
              borderRadius: '6px',
              fontSize: '13px',
              marginBottom: '18px',
              border: '1px solid #fc8181'
            }}>
              {error}
            </div>
          )}
        </form>
        </div>

        {/* Footer - Fixed */}
        <div style={{
          padding: '16px 24px',
          borderTop: '2px solid #e2e8f0',
          display: 'flex',
          gap: '10px',
          justifyContent: 'flex-end',
          flexShrink: 0,
          backgroundColor: '#f7fafc',
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px'
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '9px 18px',
              fontSize: '13px',
              fontWeight: '600',
              color: '#4a5568',
              backgroundColor: '#e2e8f0',
              border: 'none',
              borderRadius: '6px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="report-issue-form"
            disabled={submitting}
            style={{
              padding: '9px 20px',
              fontSize: '13px',
              fontWeight: '600',
              color: '#fff',
              backgroundColor: submitting ? '#cbd5e0' : '#667eea',
              border: 'none',
              borderRadius: '6px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default ReportIssueModal;
